import KeenSlider from "keen-slider";
import Layer, { EASE_OUT_QUINT } from "@/classes/Layer.js";
import { CACHE_MANAGER, overwriteCss } from "../utils.js";

const THUMBNAILS_FIRST_KEYFRAME_STYLE = { transform: "translateY(120px)" };

/**
 * @class CarouselLayer
 */
export default class CarouselLayer extends Layer {
  /**
   * @param {Spinner} spinner
   * @param {HTMLElement} node
   * @param {HTMLElement} [subjectNode]
   */
  constructor(spinner, node, subjectNode) {
    super(spinner, node, subjectNode);

    this.container = this.host.parentNode;
    this.thumbnails = [];
    this.oldThumbnail = null;
    this.activeThumbnailIndex = 0;

    this.slider = new KeenSlider(this.subject, {
      loop: true,
      selector: ".slide",
    });

    this.nodeThumbnails = this.getElmById("thumbnails");
    this.nodePrevSlide = this.getElmById("prev-slide");
    this.nodeNextSlide = this.getElmById("next-slide");
    this.nodeCloseModal = this.getElmById("close-modal");

    this.slider.on("slideChanged", (e) => {
      this.activeThumbnailIndex = e.track.details.rel;
    });

    this.slider.on("optionsChanged", (e) => {
      this.activeThumbnailIndex = e.track.details.rel;
    });

    this.nodePrevSlide.addEventListener("pointerup", () => this.slider.prev());
    this.nodeNextSlide.addEventListener("pointerup", () => this.slider.next());
    this.nodeCloseModal.addEventListener("pointerup", this.close.bind(this));
    this.host.addEventListener("pointerup", this.onHostPointerUp.bind(this));

    spinner.on("keydown", (event) => {
      if (!this.isOpen) return;

      try {
        switch (event.key) {
          case "Escape":
            this.close();
            break;
          case "ArrowLeft":
            this.slider.prev();
            break;
          case "ArrowRight":
            this.slider.next();
            break;
        }
      } catch {
        // Do nothing
      }
    });
  }

  get isOpen() {
    return this.host.style.display !== "none";
  }

  set activeThumbnailIndex(value) {
    if (this.oldThumbnail) {
      this.oldThumbnail.classList.remove("active");
    }

    this.oldThumbnail = this.thumbnails[value];
    if (this.oldThumbnail) {
      this.oldThumbnail.classList.add("active");
      this.nodeThumbnails.scrollTo({
        left:
          this.oldThumbnail.offsetLeft +
          this.oldThumbnail.offsetWidth / 2 -
          this.nodeThumbnails.offsetWidth / 2,
        behavior: "smooth",
      });
    }
  }

  set sliderThumbnailsVisibility(value) {
    if (value) {
      this.animateNode(
        this.nodeThumbnails,
        [THUMBNAILS_FIRST_KEYFRAME_STYLE, { transform: "translateY(0px)" }],
        {
          delay: 300,
          duration: 500,
          easing: EASE_OUT_QUINT,
        },
      );
    } else {
      overwriteCss(this.nodeThumbnails, THUMBNAILS_FIRST_KEYFRAME_STYLE);
    }
  }

  set sliderArrowsVisibility(value) {
    const display = value ? "block" : "none";
    this.nodePrevSlide.style.display = display;
    this.nodeNextSlide.style.display = display;
  }

  clear() {
    this.subject.innerHTML = "";
    this.nodeThumbnails.innerHTML = "";
    this.thumbnails = [];
    this.oldThumbnail = null;
  }

  /**
   * @param {HTMLElement} slideContainer
   * @param {HTMLElement} slideThumbnail
   */
  addSlide(slideContainer, slideThumbnail) {
    this.subject.appendChild(slideContainer);
    this.nodeThumbnails.appendChild(slideThumbnail);
    this.thumbnails.push(slideThumbnail);
  }

  expandFrom(originRect) {
    const spinnerRect = this.container.getBoundingClientRect();
    const originScale = 0.05;
    const originX = originRect.x + originRect.width / 2;
    const originY = originRect.y + originRect.height / 2;
    const originWidth = (spinnerRect.width * originScale) / 2;
    const originHeight = (spinnerRect.height * originScale) / 2;

    const offsetX = originX - originWidth - spinnerRect.x;
    const offsetY = originY - originHeight - spinnerRect.y;
    const subjectStartKeyframe = {
      opacity: 1,
      transform: `translate(${offsetX}px, ${offsetY}px) scale(${originScale})`,
    };

    this.animateNode(
      this.subject,
      [
        subjectStartKeyframe,
        {
          opacity: 1,
          transform: "translate(0px, 0px) scale(1)",
        },
      ],
      {
        easing: EASE_OUT_QUINT,
        duration: 500,
      },
      () => {
        overwriteCss(this.nodeThumbnails, THUMBNAILS_FIRST_KEYFRAME_STYLE);
        overwriteCss(this.subject, subjectStartKeyframe);
      },
      () => {
        this.slider.update({ loop: true, selector: ".slide" }, 0);
      },
    );

    this.sliderThumbnailsVisibility = true;
  }

  slideUp() {
    const subjectStartKeyframe = {
      opacity: 0,
      transform: `translateY(100px) scale(1)`,
    };
    this.animateNode(
      this.subject,
      [
        subjectStartKeyframe,
        {
          opacity: 1,
          transform: "translateY(0px) scale(1)",
        },
      ],
      {
        delay: 200,
        easing: EASE_OUT_QUINT,
        duration: 500,
      },
      () => {
        overwriteCss(this.nodeThumbnails, THUMBNAILS_FIRST_KEYFRAME_STYLE);
        overwriteCss(this.subject, subjectStartKeyframe);
      },
      () => {
        this.slider.update({ loop: true, selector: ".slide" }, 0);
      },
    );

    this.sliderThumbnailsVisibility = true;
  }

  ready() {
    this.sliderThumbnailsVisibility = false;
    this.sliderArrowsVisibility = false;
    this.subject.style.opacity = "0";
    this.subject.style.transform = "translateY(100px) scale(0.05)";

    this.animateNode(
      this.subject.parentNode,
      [{ opacity: 0 }, { opacity: 1 }],
      {
        easing: EASE_OUT_QUINT,
        duration: 200,
      },
      () => {
        this.subject.parentNode.style.display = "";
      },
    );
  }

  /**
   * @param {PointerEvent} event
   */
  onHostPointerUp(event) {
    if (!this.isOpen || event.button === 2 || event.pointerType === "touch") {
      return;
    }

    if (!(event.target instanceof Element)) {
      return;
    }

    if (
      this.nodeCloseModal.contains(event.target) ||
      this.nodePrevSlide.contains(event.target) ||
      this.nodeNextSlide.contains(event.target)
    ) {
      return;
    }

    const clickedOnImage = event.target.closest(".image-box");
    const clickedOnThumbnails = this.nodeThumbnails.contains(event.target);

    if (!clickedOnImage && !clickedOnThumbnails) {
      this.close();
    }
  }

  close() {
    this.animateNode(
      this.host,
      [{ opacity: 1 }, { opacity: 0 }],
      {
        duration: 200,
      },
      null,
      () => {
        this.sliderThumbnailsVisibility = false;
        this.host.style.display = "none";
      },
    );
  }

  /**
   * @param {{hash: string, row?: number, column?: number}} data
   * @returns {object}
   */
  createSlide(data) {
    const slideContainer = document.createElement("div");
    slideContainer.className = "slide";

    const slideImage = document.createElement("img");
    slideImage.className = "image-box";
    slideContainer.appendChild(slideImage);

    const slideThumbnail = document.createElement("img");
    slideThumbnail.__slideIndex = this.thumbnails.length;
    slideThumbnail.dataset.slideIndex = `${this.thumbnails.length}`;
    slideThumbnail.onpointerup = (event) => {
      if (event.button === 2) return;

      this.slider.moveToIdx(slideThumbnail.__slideIndex, true, { duration: 0 });
    };

    slideThumbnail.src = `${this.imagesBaseUrl}/${data.hash}.120`;

    this.addSlide(slideContainer, slideThumbnail);

    return { slideContainer, slideImage, slideThumbnail };
  }

  /**
   * @param {string[] | {hash: string, row?: any, column?: any}[]} items
   * @returns {Promise<string|void>}
   */
  setSlides(items) {
    this.clear();
    let firstPromise = null;

    for (const item of items) {
      const slideData =
        typeof item === "object" ? item : { hash: item, row: 0, column: 0 };

      const { slideImage } = this.createSlide(slideData);

      const fullUrl = `${this.imagesBaseUrl}/${slideData.hash}.1920`;

      if (!firstPromise) {
        firstPromise = CACHE_MANAGER.image(
          `${this.imagesBaseUrl}/${slideData.hash}.1920`,
          true,
        );
        void firstPromise.then((image) => (slideImage.src = image.blob));
      } else {
        slideImage.src = fullUrl;
      }
    }

    return firstPromise ?? Promise.resolve();
  }
}
